/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { CellError, ErrorType } from '../Cell';
import { ErrorMessage } from '../error-message';
import { isExtendedNumber } from './InterpreterValue';
export class ArrayData {
    constructor(size, data, _hasOnlyNumbers) {
        this.size = size;
        this.data = data;
        this._hasOnlyNumbers = _hasOnlyNumbers;
    }
    range() {
        return undefined;
    }
    hasOnlyNumbers() {
        return this._hasOnlyNumbers;
    }
    topLeftCorner() {
        if (this.size.height > 0 && this.size.width > 0) {
            return this.data[0][0];
        }
        return undefined;
    }
    valuesFromTopLeftCorner() {
        const ret = [];
        for (let i = 0; i < this.size.height; i++) {
            for (let j = 0; j < this.size.width; j++) {
                ret.push(this.data[i][j]);
            }
        }
        return ret;
    }
    *iterateValuesFromTopLeftCorner() {
        for (let i = 0; i < this.size.height; i++) {
            for (let j = 0; j < this.size.width; j++) {
                yield this.data[i][j];
            }
        }
    }
    raw() {
        return this.data;
    }
    rawNumbers() {
        if (this.hasOnlyNumbers()) {
            return this.data;
        }
        else {
            throw new Error('Data is not only numbers');
        }
    }
}
export class OnlyRangeData {
    constructor(size, _range, dependencyGraph) {
        this.size = size;
        this._range = _range;
        this.dependencyGraph = dependencyGraph;
    }
    raw() {
        this.ensureThatComputed();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.data;
    }
    rawNumbers() {
        if (this.hasOnlyNumbers()) {
            return this.data;
        }
        else {
            throw new Error('Data is not only numbers');
        }
    }
    hasOnlyNumbers() {
        this.ensureThatComputed();
        if (this._hasOnlyNumbers === undefined) {
            for (const v of this.iterateValuesFromTopLeftCorner()) {
                if (typeof v !== 'number') {
                    this._hasOnlyNumbers = false;
                    break;
                }
            }
            this._hasOnlyNumbers = true;
        }
        return this._hasOnlyNumbers;
    }
    topLeftCorner() {
        if (this.data !== undefined && this.size.height > 0 && this.size.width > 0) {
            return this.data[0][0];
        }
        return undefined;
    }
    range() {
        return this._range;
    }
    valuesFromTopLeftCorner() {
        this.ensureThatComputed();
        const ret = [];
        for (let i = 0; i < this.data.length; i++) {
            for (let j = 0; j < this.data[0].length; j++) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ret.push(this.data[i][j]);
            }
        }
        return ret;
    }
    *iterateValuesFromTopLeftCorner() {
        this.ensureThatComputed();
        for (let i = 0; i < this.data.length; i++) {
            for (let j = 0; j < this.data[0].length; j++) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                yield this.data[i][j];
            }
        }
    }
    ensureThatComputed() {
        if (this.data === undefined) {
            this.data = this.computeDataFromDependencyGraph();
        }
    }
    computeDataFromDependencyGraph() {
        const result = [];
        let i = 0;
        let row = [];
        for (const cellFromRange of this._range.addresses(this.dependencyGraph)) {
            const value = this.dependencyGraph.getCellValue(cellFromRange);
            if (value instanceof SimpleRangeValue) {
                row.push(new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected));
            }
            else if (isExtendedNumber(value)) {
                row.push(value);
            }
            else {
                row.push(value);
                this._hasOnlyNumbers = false;
            }
            ++i;
            if (i % this.size.width === 0) {
                i = 0;
                result.push([...row]);
                row = [];
            }
        }
        return result;
    }
}
export class SimpleRangeValue {
    constructor(data, _range) {
        this.data = data;
        this._range = _range;
    }
    get size() {
        return this.data.size;
    }
    static onlyNumbersDataWithRange(data, size, range) {
        return new SimpleRangeValue(new ArrayData(size, data, true), range);
    }
    static onlyNumbersDataWithoutRange(data, size) {
        return new SimpleRangeValue(new ArrayData(size, data, true));
    }
    static onlyRange(range, dependencyGraph) {
        return new SimpleRangeValue(new OnlyRangeData({
            width: range.width(),
            height: range.height()
        }, range, dependencyGraph));
    }
    static fromScalar(scalar) {
        const hasOnlyNumbers = isExtendedNumber(scalar);
        return new SimpleRangeValue(new ArrayData({ width: 1, height: 1 }, [[scalar]], hasOnlyNumbers));
    }
    width() {
        return this.data.size.width;
    }
    height() {
        return this.data.size.height;
    }
    raw() {
        return this.data.raw();
    }
    topLeftCornerValue() {
        return this.data.topLeftCorner();
    }
    valuesFromTopLeftCorner() {
        return this.data.valuesFromTopLeftCorner();
    }
    *iterateValuesFromTopLeftCorner() {
        yield* this.data.iterateValuesFromTopLeftCorner();
    }
    numberOfElements() {
        return this.data.size.width * this.data.size.height;
    }
    hasOnlyNumbers() {
        return this.data.hasOnlyNumbers();
    }
    rawNumbers() {
        return this.data.rawNumbers();
    }
    range() {
        var _a;
        return (_a = this._range) !== null && _a !== void 0 ? _a : this.data.range();
    }
    sameDimensionsAs(other) {
        return this.width() === other.width() && this.height() === other.height();
    }
}
